import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, FileUp, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  getListCareProtocolOpportunitiesQueryKey,
  useDismissCareProtocolOpportunity,
  useListCareGapUploadDocuments,
} from "@elation/api-client/src/stitch-web";
import type { CareProtocolOpportunityListItem } from "@elation/api-client/src/stitch-web/models";

import { PatientEntity } from "@src/features/patients";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/shared/components/ui/collapsible";
import { navigationV2 } from "@src/shared/navigation/v2";
import { pluralize } from "@src/shared/utils/strings";

import { CareProtocolOpportunityEntity } from "../../../../../domain";

type PatientGroup = {
  patient: CareProtocolOpportunityListItem["patient"];
  items: CareProtocolOpportunityListItem[];
};

function groupByPatient(
  items: CareProtocolOpportunityListItem[],
): PatientGroup[] {
  const groups = new Map<number, PatientGroup>();

  for (const item of items) {
    const group = groups.get(item.patient.id) ?? {
      patient: item.patient,
      items: [],
    };
    group.items.push(item);
    groups.set(item.patient.id, group);
  }

  return [...groups.values()];
}

function PatientSection({ group }: { group: PatientGroup }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dismiss = useDismissCareProtocolOpportunity({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getListCareProtocolOpportunitiesQueryKey(),
        });
      },
    },
  });

  return (
    <section className="border-t border-border">
      <Button
        type="button"
        variant="ghost"
        className="h-auto w-full justify-start gap-2.5 rounded-none bg-transparent px-4 py-2.5 text-left font-normal shadow-none hover:bg-muted/30 hover:text-foreground"
        onClick={() =>
          navigate(navigationV2.patientDetail(String(group.patient.id)))
        }
      >
        <VerticalFlexbox className="min-w-0 flex-1">
          <span className="truncate text-[13px] font-semibold text-surface-foreground">
            {PatientEntity.displayName(group.patient) ?? "Unknown patient"}
          </span>
          <span className="mt-0.5 font-mono text-[10px] text-muted-foreground">
            {[
              group.patient.dob.replaceAll("-", "/"),
              `${group.patient.age}${PatientEntity.sexAbbreviation(group.patient.sex) ?? ""}`,
              pluralize(group.items.length, "item"),
            ].join(" · ")}
          </span>
        </VerticalFlexbox>
        <span className="inline-flex shrink-0 items-center gap-1 font-mono text-[10px] text-primary">
          View
          <ChevronRight className="size-[13px]" aria-hidden="true" />
        </span>
      </Button>
      <VerticalFlexbox className="gap-1 px-4 pb-2.5">
        {group.items.map((item) => (
          <HorizontalFlexbox
            key={item.id}
            alignItems="center"
            className="group gap-[9px] rounded-[8px] px-2 py-[5px] hover:bg-muted/30"
          >
            <span
              className="size-[5px] shrink-0 rounded-full bg-primary"
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 text-[12.5px] text-surface-foreground">
              {item.description}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-[26px] shrink-0 rounded-[6px] text-muted-foreground hover:bg-background"
              aria-label={`Remove ${item.description}`}
              disabled={dismiss.isPending}
              onClick={() => dismiss.mutate({ id: item.id })}
            >
              <X className="size-3.5" aria-hidden="true" />
            </Button>
          </HorizontalFlexbox>
        ))}
      </VerticalFlexbox>
    </section>
  );
}

export function CareProtocolOpportunityUploadGroup({
  date,
  defaultOpen = false,
  items,
  uploadedAt,
}: {
  date: string;
  defaultOpen?: boolean;
  items: CareProtocolOpportunityListItem[];
  uploadedAt: string;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const documentsQuery = useListCareGapUploadDocuments({ date });
  const patientGroups = useMemo(() => groupByPatient(items), [items]);
  const fileCount = documentsQuery.data?.data.documents.length ?? 0;

  return (
    <li>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="overflow-hidden rounded-[12px] border border-border bg-background shadow-none">
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="h-auto w-full justify-start gap-[11px] rounded-none bg-muted/35 px-3.5 py-3 text-left font-normal shadow-none hover:bg-muted/55 hover:text-foreground"
            >
              <ChevronRight
                className={`size-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}
                aria-hidden="true"
              />
              <div className="flex size-[30px] shrink-0 items-center justify-center rounded-[8px] bg-primary/10 text-primary">
                <FileUp className="size-[15px]" aria-hidden="true" />
              </div>
              <VerticalFlexbox className="min-w-0 flex-1">
                <span className="truncate text-[13.5px] font-semibold text-surface-foreground">
                  {CareProtocolOpportunityEntity.uploadTimeLabel(uploadedAt)}
                </span>
                <span className="mt-0.5 font-mono text-[10.5px] text-muted-foreground">
                  {[
                    CareProtocolOpportunityEntity.fileCountLabel(fileCount),
                    CareProtocolOpportunityEntity.patientCountLabel(items),
                    CareProtocolOpportunityEntity.itemCountLabel(items),
                  ].join(" · ")}
                </span>
              </VerticalFlexbox>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            {patientGroups.map((group) => (
              <PatientSection key={group.patient.id} group={group} />
            ))}
          </CollapsibleContent>
        </div>
      </Collapsible>
    </li>
  );
}
